import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  required?: boolean;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "email-address" | "phone-pad" | "numeric";
  colors: ReturnType<typeof useThemeColors>;
}

const FormInput = ({
  label,
  value,
  onChangeText,
  placeholder,
  required = false,
  autoCapitalize = "words",
  keyboardType = "default",
  colors,
}: FormInputProps) => (
  <View className="mb-4">
    <Text className="text-sm font-medium mb-1.5" style={{ color: colors.text }}>
      {label}
      {required && <Text style={{ color: "#DC2626" }}> *</Text>}
    </Text>
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.textTertiary}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType}
      className="px-4 py-3 rounded-xl text-base"
      style={{
        backgroundColor: colors.background,
        borderWidth: 1,
        borderColor: colors.border,
        color: colors.text,
      }}
    />
  </View>
);

/**
 * Date picker input (simplified as text for now)
 */
interface DateInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  colors: ReturnType<typeof useThemeColors>;
}

const DateInput = ({ label, value, onChangeText, colors }: DateInputProps) => (
  <View className="mb-4">
    <Text className="text-sm font-medium mb-1.5" style={{ color: colors.text }}>
      {label}
    </Text>
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder="DD-MM-JJJJ"
      placeholderTextColor={colors.textTertiary}
      keyboardType="numeric"
      className="px-4 py-3 rounded-xl text-base"
      style={{
        backgroundColor: colors.background,
        borderWidth: 1,
        borderColor: colors.border,
        color: colors.text,
      }}
    />
    <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
      Formaat: DD-MM-JJJJ (bijv. 15-03-2015)
    </Text>
  </View>
);

/**
 * Create dependent user screen
 * Allows super admins to create users without email (kids)
 */
export default function CreateDependentScreen() {
  const router = useRouter();
  const { user: currentUser } = useAuth();
  const colors = useThemeColors();

  const [fullName, setFullName] = useState("");
  const [nickname, setNickname] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Parse date string (DD-MM-YYYY) to ISO format
   */
  const parseDateToISO = (dateStr: string): string | null => {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;

    const [day, month, year] = parts;
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    // Basic validation
    if (
      isNaN(dayNum) ||
      isNaN(monthNum) ||
      isNaN(yearNum) ||
      dayNum < 1 ||
      dayNum > 31 ||
      monthNum < 1 ||
      monthNum > 12 ||
      yearNum < 1900 ||
      yearNum > new Date().getFullYear()
    ) {
      return null;
    }

    // Return ISO format (YYYY-MM-DD)
    return `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
  };

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    if (!fullName.trim()) {
      Alert.alert("Fout", "Vul een naam in");
      return false;
    }

    if (fullName.trim().length < 2) {
      Alert.alert("Fout", "Naam moet minimaal 2 tekens bevatten");
      return false;
    }

    if (dateOfBirth.trim() && !parseDateToISO(dateOfBirth)) {
      Alert.alert("Fout", "Ongeldige geboortedatum. Gebruik formaat DD-MM-JJJJ");
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Parse date of birth if provided
      const parsedDOB = dateOfBirth.trim() ? parseDateToISO(dateOfBirth) : null;

      // Create the dependent profile using the database function
      // This bypasses RLS and handles all the logic server-side
      const { data: dependentId, error } = await supabase.rpc("create_dependent_user", {
        p_full_name: fullName.trim(),
        p_nickname: nickname.trim() || null,
        p_date_of_birth: parsedDOB,
        p_created_by: currentUser?.id,
      });

      if (error) {
        console.error("Error creating dependent:", error);
        throw error;
      }

      // Log the creation in activity logs
      await supabase.from("activity_logs").insert({
        user_id: currentUser?.id,
        action_type: "admin.user_create_dependent",
        action_category: "admin",
        target_type: "profile",
        target_id: dependentId,
        target_name: fullName.trim(),
        metadata: {
          dependent_name: fullName.trim(),
          nickname: nickname.trim() || null,
          date_of_birth: parsedDOB,
        },
      });

      Alert.alert(
        "Succes",
        `${fullName.trim()} is aangemaakt als kind gebruiker. Je kunt deze gebruiker nu toevoegen aan een team.`,
        [{ text: "OK", onPress: () => router.back() }]
      );
    } catch (error: any) {
      console.error("Error creating dependent:", error);
      Alert.alert(
        "Fout",
        error.message || "Kon kind gebruiker niet aanmaken. Probeer het opnieuw."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Info Banner */}
          <View
            className="mx-4 mt-4 p-4 rounded-xl flex-row"
            style={{ backgroundColor: "#EDE9FE" }}
          >
            <Ionicons name="information-circle" size={24} color="#7C3AED" />
            <View className="flex-1 ml-3">
              <Text className="font-semibold" style={{ color: "#5B21B6" }}>
                Kind Gebruiker
              </Text>
              <Text className="text-sm mt-1" style={{ color: "#6D28D9" }}>
                Een kind gebruiker heeft geen eigen e-mailadres of telefoon. Een
                guardian (familie lid met een account) kan inloggen als dit kind om
                mee te doen aan het weekend.
              </Text>
            </View>
          </View>

          {/* Form */}
          <View className="p-4 mt-2">
            <FormInput
              label="Volledige Naam"
              value={fullName}
              onChangeText={setFullName}
              placeholder="Bijv. Zoë Staats"
              required
              colors={colors}
            />

            <FormInput
              label="Bijnaam"
              value={nickname}
              onChangeText={setNickname}
              placeholder="Bijv. Zootje"
              colors={colors}
            />

            <DateInput
              label="Geboortedatum"
              value={dateOfBirth}
              onChangeText={setDateOfBirth}
              colors={colors}
            />
          </View>

          {/* Next Steps Info */}
          <View className="mx-4 p-4 rounded-xl" style={{ backgroundColor: colors.surface }}>
            <Text className="font-semibold mb-2" style={{ color: colors.text }}>
              Na het aanmaken:
            </Text>
            <View className="flex-row items-start mb-2">
              <Text style={{ color: colors.primary }}>1.</Text>
              <Text className="flex-1 ml-2" style={{ color: colors.textSecondary }}>
                Voeg het kind toe aan een team (Familie Admin)
              </Text>
            </View>
            <View className="flex-row items-start mb-2">
              <Text style={{ color: colors.primary }}>2.</Text>
              <Text className="flex-1 ml-2" style={{ color: colors.textSecondary }}>
                Wijs een guardian toe uit het team (Familie Admin)
              </Text>
            </View>
            <View className="flex-row items-start">
              <Text style={{ color: colors.primary }}>3.</Text>
              <Text className="flex-1 ml-2" style={{ color: colors.textSecondary }}>
                De guardian kan dan "inloggen als" dit kind
              </Text>
            </View>
          </View>

          {/* Spacer */}
          <View className="h-24" />
        </ScrollView>

        {/* Submit Button */}
        <View
          className="absolute bottom-0 left-0 right-0 p-4"
          style={{
            backgroundColor: colors.background,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Pressable
            onPress={handleSubmit}
            disabled={isSubmitting || !fullName.trim()}
            className="py-4 rounded-xl items-center active:opacity-80"
            style={{
              backgroundColor: !fullName.trim() ? colors.border : "#7C3AED",
              opacity: isSubmitting ? 0.7 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel="Kind gebruiker aanmaken"
          >
            {isSubmitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold text-base">
                Kind Aanmaken
              </Text>
            )}
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

